import type { LedgerEntry, PaymentAllocation } from '@/entities/ledger/ledger-entry.entity';
import type { InvoiceAllocation } from '@/entities/ledger/invoice-allocation.entity';
import { httpClient } from './http-client';
import type {
  AllocateInvoiceToLedgerEntriesDto,
  AllocatePaymentToLedgerEntriesDto,
  ItemsToNetDto,
  LedgerControllerFindAllLedgerEntriesData,
  RecalculateLedgerEntriesDto,
} from '@carvia/ros-client-types';
import type { PaginatedResponse } from '@/entities/pagination/paginated-response';

class LedgerService {
  endpoint = '/ledger';

  async findAllLedgerEntries(query: LedgerControllerFindAllLedgerEntriesData['query']) {
    const res = await httpClient.get<PaginatedResponse<LedgerEntry>>(
      this.endpoint + '/ledger-entries',
      {
        params: query,
      },
    );
    return res.data;
  }

  async recalculateLedgerEntries(dto: RecalculateLedgerEntriesDto): Promise<void> {
    await httpClient.post(`${this.endpoint}/recalculate-ledger-entries`, dto);
  }

  async getPaymentAllocationsByPaymentId(paymentId: string): Promise<PaymentAllocation[]> {
    const res = await httpClient.get<PaymentAllocation[]>(
      `${this.endpoint}/payment-allocations/${paymentId}`,
    );
    return res.data;
  }

  async getInvoiceAllocationsByInvoiceId(invoiceId: string): Promise<InvoiceAllocation[]> {
    const res = await httpClient.get<InvoiceAllocation[]>(
      `${this.endpoint}/invoice-allocations/${invoiceId}`,
    );
    return res.data;
  }

  async allocatePaymentToLedgerEntries(
    dto: AllocatePaymentToLedgerEntriesDto,
  ): Promise<PaymentAllocation[]> {
    const res = await httpClient.post<PaymentAllocation[]>(
      `${this.endpoint}/allocate-payment`,
      dto,
    );
    return res.data;
  }

  async allocateInvoiceToLedgerEntries(
    dto: AllocateInvoiceToLedgerEntriesDto,
  ): Promise<InvoiceAllocation[]> {
    const res = await httpClient.post<InvoiceAllocation[]>(
      `${this.endpoint}/allocate-invoice`,
      dto,
    );
    return res.data;
  }

  async checkNettingEligibility(bookingId: string) {
    const res = await httpClient.get<ItemsToNetDto>(
      `${this.endpoint}/check-netting-eligibility/${bookingId}`,
    );
    return res.data;
  }

  async autoNetBookingLedgerEntries(bookingId: string): Promise<number> {
    const res = await httpClient.post<number>(
      `${this.endpoint}/auto-net-booking-ledger-entries/${bookingId}`,
    );
    return res.data;
  }
}

export const ledgerService = new LedgerService();
